import {
  ModeEnum,
  ProductTypeEnum,
  SettleValueTypeEnum,
  type DerivativeSettlementTypeEnum,
  type FeeModeEnum,
  type ISecurity,
  type InstrumentCompositionEnum,
  type LegDirectionEnum,
  type OptionTypeEnum,
  type SyntheticProductTypeEnum,
} from './types';

export interface MultilegMarketDetails {
  Market: string;
  MarketAccount: string;
}

// This type is just based on the latest understanding of the backend. This is a very handwavey type (its "arbitrary" metadata)
export interface SecurityMetadata extends Record<string, unknown> {
  /** US bond/security identifier. */
  CUSIP?: string;
  /** Bloomberg global identifier. */
  FIGI?: string;
  /** International security identifier. */
  ISIN?: string;
  /** UK/Ireland security identifier. */
  SEDOL?: string;
  /** Normalized issue type; defaults from BondType if absent during import. */
  IssueType?: string;
  /** Bond category/type from secmaster input. */
  BondType?: string;
  /** Original issue tenor/term. */
  IssueTerm?: string;
  /** Date the bond/security was issued. */
  IssueDate?: string;
  /** Coupon rate as a decimal string; zero is preserved as "0". */
  CouponRate?: string;
  /** Last/reference price, parsed as a decimal when importing/exporting. */
  LastPrice?: string;
  /** DEX display name for securities backed by decentralized exchanges. */
  DexName?: string;
}

export interface Security extends Partial<Pick<ISecurity, 'ProductDetails' | 'SubProductType'>> {
  Timestamp: string;
  Symbol: string;
  MinAmtIncrement?: string;
  MinPriceIncrement: string;
  MinSizeIncrement: string;
  MinimumSize: string;
  MaximumSize: string;
  MinimumAmount?: string;
  QuoteCurrency: string;
  /** BaseCurrency can be undefined for Compound instruments, for example */
  BaseCurrency: string | undefined;
  DefaultPriceIncrement: string;
  DefaultSizeIncrement: string;
  PriceDisplaySpec: string;
  SizeDisplaySpec: string;
  NormalSize: string;
  ProductType: ProductTypeEnum;
  PositionCurrency?: string;
  SettlementCurrency: string;
  SettleValueType?: SettleValueTypeEnum;
  DerivativeSettlementType?: DerivativeSettlementTypeEnum;
  NotionalMultiplier: string;
  DisplaySymbol: string;
  Metadata?: string | SecurityMetadata;
  Description: string;
  Markets?: string[];
  Expiration?: string;
  UnderlyingQuoteCurrency?: ISecurity['UnderlyingQuoteCurrency'];
  UnderlyingSymbol?: ISecurity['UnderlyingSymbol'];
  OptionType?: OptionTypeEnum;
  StrikePrice?: string;
  Mode?: ModeEnum;
  DefaultPricingRuleMode?: ModeEnum;
  Rank?: number;
  Composition?: InstrumentCompositionEnum;
  /** ID of the prediction-outcome event this outcome security belongs to */
  PredictionOutcomeEventID?: ISecurity['PredictionOutcomeEventID'];
  EndTime?: string;
  SupportedPricingModes?: {
    ImpliedVolatility?: boolean;
    UnderlyingQuoteCurrency?: boolean;
    BPS?: boolean;
  };
  // Generated locally to help with autocompletes
  searchSymbol?: string;
  MultilegDetails?: {
    FeeMode: FeeModeEnum;
    SyntheticProductType: SyntheticProductTypeEnum;
    MaxMarketDataDepth: number;
    Legs: MultilegDetailsLeg[];
    Parameters: {
      LegParams: (Record<string, string | number | boolean> & {
        Initiating: boolean;
        HaircutRatio?: string;
      })[];
    };
    // TODO: Need to confirm if this is always expected or not, but seen in the data feed
    ProductType?: ProductTypeEnum;
  };
  Legs?: {
    Direction: LegDirectionEnum;
    Symbol: string;
    SizeRatio?: string;
  }[];
}

export interface MultilegDetailsLeg {
  Direction: LegDirectionEnum;
  Index: number;
  Markets?: MultilegMarketDetails[] | null;
  Symbol: string;
}

export const EMPTY_SECURITY: Security & { SizeBuckets: unknown[] } = {
  Timestamp: '',
  Symbol: '',
  MinPriceIncrement: '',
  MinSizeIncrement: '',
  MinimumSize: '',
  MaximumSize: '',
  QuoteCurrency: '',
  BaseCurrency: '',
  DefaultPriceIncrement: '',
  DefaultSizeIncrement: '',
  PriceDisplaySpec: '',
  SizeDisplaySpec: '',
  NormalSize: '',
  ProductType: ProductTypeEnum.Spot,
  PositionCurrency: '',
  SettlementCurrency: '',
  SettleValueType: SettleValueTypeEnum.Regular,
  NotionalMultiplier: '',
  DisplaySymbol: '',
  Description: '',
  SizeBuckets: [],
  Markets: [],
  Expiration: '',
  Mode: ModeEnum.Enabled,
  DefaultPricingRuleMode: ModeEnum.Enabled,
};

// Mirrors the backend API
export interface MultilegModel {
  Symbol: string;
  Description: string;
  DisplaySymbol: string;
  ProductType: ProductTypeEnum;
  SyntheticProductType: SyntheticProductTypeEnum;
  MinSizeIncrement: string;
  MinPriceIncrement: string;
  MaxMarketDataDepth: number;
  FeeMode?: FeeModeEnum;
  ReqLegs: {
    Symbol: string;
    Markets: string[];
  }[];
  Parameters: {
    LegParams: {
      Initiating: boolean;
      HaircutRatio?: string;
    }[];
  };
}
